<template>
  <div>
    <h2 class="display-4 mt-5">Uno Apple Juice!</h2>
    <p>Learn verb conjuctions and make the leaderboard.</p>
    <div class="my-3 d-flex justify-content-center">
      <Leaderboard
        ref="leaderboard"
        class="bg-light p-3 border"
        style="max-width: 350px"
      />
    </div>
    <div
      v-tooltip="{
        content: 'You cannot change pronouns and tenses in game mode',
        disabled: !isGameMode,
      }"
    >
      <div
        class="d-flex align-items-center justify-content-center my-3"
        :style="{
          'pointer-events': isGameMode ? 'none' : '',
          opacity: isGameMode ? 0.5 : 1,
        }"
      >
        <pronoun-menu />
        <tense-menu v-if="!isGameMode || difficulty === 'easy'" class="ms-3" />
        <input
          type="checkbox"
          v-model="isAutoSkip"
          class="form-check-input ms-3 me-2 mt-0"
          id="gameModeToggle"
        />
        <label class="form-check-label" for="gameModeToggle"> Auto Skip </label>
      </div>
    </div>
    <div class="mode-selector mb-3">
      <div v-if="!isGameMode" class="btn-group btn-group-sm me-2">
        <button
          v-for="d in difficulties"
          :key="d"
          :class="[
            'btn fw-bold',
            'btn-outline-secondary',
            { active: difficulty === d },
          ]"
          @click="$store.dispatch('setDifficulty', d)"
        >
          {{ d }}
        </button>
      </div>
    </div>
    <button @click="newGame" class="btn btn-lg btn-secondary fw-bold mb-3">
      {{ isGameMode ? "Exit Game" : "Start Game" }}
    </button>
    <div class="game-view" :class="{ 'game-mode': isGameMode }" ref="gameView">
      <div v-if="isGameMode" ref="scoreboard" class="scoreboard mb-lg-3">
        <div class="progress d-none d-lg-block">
          <div
            class="progress-bar"
            role="progressbar"
            :style="{ width: `${progress}%` }"
            :aria-valuenow="progress"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
        <div
          class="d-flex justify-content-between align-items-center px-2 px-md-5 flex-row"
        >
          <div class="score">
            <span class="d-none d-sm-inline">Score:&nbsp;</span>
            <b
              class="d-inline-block transition-all"
              :class="{ 'text-success pulse': isTweening }"
              >{{ tweened.score.toFixed(0) }} pts</b
            >
            <em
              class="badge rounded-pill bg-info text-dark position-absolute mode-label"
              style="top: 5px; font-size: 10px"
              >{{ difficulty }}</em
            >
            <!-- / {{ (questionsAsked.length - 1) * 3 }} -->
          </div>
          <div class="d-none d-md-block">
            <button
              v-if="currentChosenAnswer"
              class="btn btn-primary text-light fw-bold"
              @click="newQuestion"
            >
              {{ remainingLives > 0 ? "Next" : "Finish" }} →
            </button>
          </div>
          <div class="lives-container">
            <div class="heart">
              {{ displayLives }}
            </div>
            <!-- <span v-for="(heart, index) in 3" :key="index" class="heart">
            {{ index < remainingLives ? "❤️" : "🤍" }}
          </span> -->
          </div>
        </div>

        <em ref="question" class="question question-en fw-bold mt-2">{{
          selectedExample.en
        }}</em>
        <div class="text-muted question question-es">
          <!-- Hint: -->
          <em v-html="currentQuestionMasked"></em>
          <a
            href="javascript:void(0)"
            class="text-decoration-none ms-2"
            :class="{'fade-buttons': isPlayingAudio}"
            @click="playAudioFile"
            :disabled="isPlayingAudio"
            v-tooltip="{
              content: 'Listen to the example en español',
              disabled: isHintShown,
            }"
          >
           {{ !isPlayingAudio ? '🔈' : '🔊'}}</a
          >
        </div>
      </div>
      <div
        v-else-if="selectedExample"
        class="alert alert-light my-3"
        style="position: sticky; top: 0; z-index: 10"
      >
        <strong v-if="!isGameMode" class="d-block"
          >{{ selectedExample.es }}
          <a
            href="javascript:void(0)"
            class="text-decoration-none ms-1"
            @click="speakText(selectedExample.es)"
            v-tooltip="{
              content: 'Listen to the example en español',
            }"
          >
            🔈</a
          ></strong
        >
        <em>{{ selectedExample.en }}</em>
      </div>
      <div
        class="mobile-version d-flex flex-column d-lg-none"
        ref="mobileView"
        v-if="isGameMode"
      >
        <div v-if="pickedVerb === null" class="mobile-selection">
          <button
            v-for="(verb, index) in allVerbs"
            :key="index"
            class="btn btn-secondary fw-bold"
            @click="answerVerb(index)"
          >
            {{ verb.name }}
          </button>
          <speech-recognition @transcript="onTranscript" @end="onSpeechEnd" />
        </div>

        <div v-else class="mobile-selection">
          <template v-for="pronoun in selectedPronouns" :key="pronoun">
            <div
              class="tense-mobile-item"
              :class="{
                answered: currentChosenAnswer,
              }"
              v-for="(tense, tKey) in tenses"
              :key="tKey"
            >
              <small
                v-if="difficulty === 'easy'"
                class="text-muted d-block mb-1 text-truncate"
              >
                {{ tense }} / {{ pronoun }}
              </small>
              <button
                class="btn w-100 fw-bold btn-info btn-answer"
                :class="getAnswerButtonClasses(pronoun, tKey)"
                @click="clickAnswer(pickedVerb, tKey, pronoun)"
              >
                {{
                  allVerbs[pickedVerb].conjugations[tKey][pronoun].conjugation
                }}
              </button>
            </div>
          </template>
          <button
            v-if="!currentChosenAnswer"
            class="btn btn-link"
            @click="pickedVerb = null"
          >
            ← Back
          </button>
        </div>
        <div class="d-flex align-items-center justify-content-end"></div>
        <button
          v-if="currentChosenAnswer"
          class="btn btn-primary text-light fw-bold"
          @click="newQuestion"
        >
          {{ remainingLives > 0 ? "Next" : "Finish" }} →
        </button>
      </div>
      <!-- <div class="align-items-center mb-3 d-none d-lg-flex">
        <b class="ms-auto">Toggle tenses:</b>
        <tense-menu class="ms-2" />
      </div> -->
      <table
        border="1"
        ref="verbsTable"
        class="table table-striped table-hover d-none d-lg-table"
        :class="{ 'game-mode _table-dark': isGameMode }"
      >
        <thead v-if="!isGameMode || difficulty === 'easy'">
          <tr>
            <th>Verb</th>
            <th v-for="tense in tenses" :key="tense">
              {{ tense }}
              <!-- <em v-if="!isGameMode" class="d-block">
              {{ tenses[key] }}
            </em> -->
            </th>
            <!-- <th>Present <em>(run)</em></th>
          <th>Preterite <em>(ran)</em></th>
          <th>Imperfect <em>(running)</em></th>
          <th>Future <em>(will run)</em></th>
          <th>Conditional <em>(would run)</em></th>
          <th>Subjunctive Present <em>(hope to run)</em></th> -->
          </tr>
        </thead>
        <tbody>
          <tr v-for="(verb, index) in allVerbs" :key="index">
            <td style="">
              <div
                class="verb-col-wrap d-flex justify-content-between align-items-center"
              >
                <div class="verb-col text-start">
                  <b>{{ verb.name }}</b>
                  <small v-if="!isGameMode" class="text-muted d-block">
                    ({{ verb.en }})</small
                  >
                </div>
                <div class="div text-end">
                  <em
                    class="d-block text-muted"
                    v-for="pronoun in selectedPronouns"
                    :key="pronoun"
                    >{{ pronoun }}</em
                  >
                </div>
              </div>
            </td>
            <td
              v-for="(tense, key) in tenses"
              :key="key + index"
              :class="{
                'border bg-white': pickedVerb == index && pickedTense == key,
              }"
            >
              <div
                class="pronouns-table-item"
                v-for="pronoun in selectedPronouns"
                :key="pronoun"
                @click="showExample(index, key, pronoun)"
                v-tooltip="{
                  content: `<b>${verb.conjugations[key][pronoun].esExample}</b><em class='d-block'>${verb.conjugations[key][pronoun].enExample}</em>`,
                  html: true,
                  disabled: isGameMode,
                }"
                :class="{
                  'fw-bold':
                    pickedVerb == index &&
                    pickedTense == key &&
                    pickedPronoun == pronoun,
                }"
              >
                {{ verb.conjugations[key][pronoun].conjugation }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <PromptDialog
      ref="promptDialog"
      title="Leaderboard"
      :details="`Tu marcador es ${score} puntos!`"
      placeholder="Entra tu nombre"
      :score="score"
    />
  </div>
</template>
  
  <script>
import verbs from "@/constants/verbs.v2";
import { TENSES } from "@/constants";
import { useToast } from "vue-toastification";
import PronounFilter from "./PronounFilter.vue";
import TenseMenu from "./TenseMenu.vue";
import Leaderboard from "./Leaderboard.vue";
import { mapGetters } from "vuex";
import PromptDialog from "./PromptDialog.vue";
import PronounMenu from "./PronounMenu.vue";
import gsap from "gsap";
import supabase from "@/supabase";
import { sounds, preloadSounds } from "../sounds";
import confetti from "canvas-confetti";
import SpeechRecognition from "./SpeechRecognition.vue";

const POINTS = {
  easy: 2,
  medium: 4,
  hard: 6,
  expert: 8,
};

export default {
  components: {
    PronounFilter,
    TenseMenu,
    Leaderboard,
    PromptDialog,
    PronounMenu,
    SpeechRecognition,
  },
  name: "VerbsTable",
  data() {
    return {
      isGameMode: false,
      pickedVerb: null,
      pickedTense: null,
      pickedPronoun: null,
      isAutoSkip: true,
      allVerbs: verbs.sort((a, b) => {
        // Get the last two characters of each verb name
        const endingA = a.name.slice(-2);
        const endingB = b.name.slice(-2);
        // Sort based on the last two characters
        if (endingA < endingB) return -1;
        if (endingA > endingB) return 1;
        return 0;
      }),
      allQuestions: [],
      questionsAsked: [],
      isHintShown: false,
      remainingLives: 3,
      nextQuestionTimeout: null,
      difficulties: ["easy", "medium", "hard", "expert"],
      tweened: { score: 0 },
      isTweening: false,
      isSpeechCorrect: false,
      isPlayingAudio: false,
    };
  },
  mounted() {
    this.shuffledQuestions();
    // this.getRandomExample();
    this.toast = useToast();
    preloadSounds();
  },
  computed: {
    ...mapGetters([
      "username",
      "currentScore",
      "tensesKeys",
      "selectedTenses",
      "selectedPronouns",
      "difficulty",
      "leaderboardMinScore",
    ]),
    displayLives() {
      if (this.remainingLives <= 0) return "🤍🤍🤍";
      const fullHearts = Math.floor(this.remainingLives / 2);
      const halfHeart = this.remainingLives % 2 ? "💔" : "";
      return (
        "❤️".repeat(fullHearts) +
        halfHeart +
        "🤍".repeat(3 - fullHearts - (halfHeart ? 1 : 0))
      );
    },
    tenses() {
      // Get the keys that are selected
      const _selectedTenses = this.tensesKeys.filter(
        (key) =>
          (this.isGameMode &&
            (this.difficulty === "hard" || this.difficulty === "expert")) ||
          this.selectedTenses.includes(key)
      );

      // Randomize the keys if the difficulty is hard
      if (this.isGameMode && this.difficulty !== "easy") {
        _selectedTenses.sort(() => Math.random() - 0.5);
        if (this.difficulty === "hard" || this.difficulty === "expert") {
          for (let i = 0; i < this.questionsAsked.length; i++)
            _selectedTenses.sort(() => Math.random() - 0.5);
        }
      }

      return _selectedTenses.reduce((obj, key) => {
        obj[key] = TENSES[key];
        return obj;
      }, {});
    },
    score: {
      get() {
        return this.currentScore;
      },
      set(newScore) {
        this.$store.dispatch("updateScore", newScore);
      },
    },
    currentQuestion() {
      return this.questionsAsked[this.questionsAsked.length - 1];
    },
    currentChosenAnswer() {
      return this.currentQuestion?.answer;
    },
    selectedExample() {
      let o = null;
      if (this.isGameMode) {
        const q = this.currentQuestion;
        if (q) o = this.allVerbs[q.verb].conjugations[q.tense][q.pronoun];
      } else if (this.pickedVerb && this.pickedTense && this.pickedPronoun) {
        o =
          this.allVerbs[this.pickedVerb].conjugations[this.pickedTense][
            this.pickedPronoun
          ];
      }

      return (
        o && {
          en: o.enExample,
          es: o.esExample,
        }
      );
    },
    currentQuestionMasked() {
      // if(this.currentChosenAnswer) return this.selectedExample.es;

      const tense =
        this.allVerbs[this.currentQuestion.verb].conjugations[
          this.currentQuestion.tense
        ][this.currentQuestion.pronoun].conjugation;
      const regex = new RegExp(tense, "i"); // "i" flag for case-insensitive matching
      return this.selectedExample.es.replace(regex, (match) => {
        return `<span class='fw-bold px-2 ${
          this.currentChosenAnswer ? "bg-secondary text-dark" : "text-muted"
        }'>${this.currentChosenAnswer ? match : "_____"}</span>`;
      });
    },
    progress() {
      return Math.min(
        (this.questionsAsked.length / this.allQuestions.length) * 100,
        100
      );
    },
  },
  watch: {
    score(n, o) {
      if (!o && n === 0) return;
      const duration = 2.6;
      this.isTweening = true;
      gsap.to(this.tweened, {
        duration,
        score: Number(n) || 0,
        onComplete: () => {
          this.isTweening = false;
        },
      });
    },
  },
  methods: {
    asExample(o) {
      return {
        en: o.enExample,
        es: o.esExample,
      };
    },
    answerVerb(verb) {
      this.pickedVerb = verb;
      this.$nextTick(() => {
        this.$refs.mobileView?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      });
    },
    async clickAnswer(verb, tense, pronoun) {
      if (this.currentQuestion.answer) return;
      this.checkAnswer(verb, tense, pronoun);
    },
    showExample(verb, tense, pronoun) {
      if (this.isGameMode) {
        this.checkAnswer(verb, tense, pronoun);
        return;
      }
      this.pickedVerb = verb;
      this.pickedTense = tense;
      this.pickedPronoun = pronoun;

      // //todo: make computed, if isGamemode use q.verb, else pickedVerb
      // this.selectedExample = this.asExample(
      //   this.allVerbs[verb].conjugations[tense][pronoun]
      // );
    },
    async newGame() {
      if (this.isGameMode) {
        await this.endGame();
        return;
      }
      this.pickedVerb = null;
      this.pickedTense = null;
      this.pickedPronoun = null;
      this.score = 0;
      this.questionsAsked = [];
      this.shuffledQuestions();
      this.remainingLives = 6;
      if (this.difficulty === "expert" || this.difficulty === "hard") {
        this.$store.dispatch("setDifficulty", this.difficulty); //ensure tenses are set
      }
      this.newQuestion();
      this.isGameMode = true;
    },
    shuffledQuestions() {
      let questions = [];
      for (let i = 0; i < this.allVerbs.length; i++) {
        for (let j = 0; j < this.selectedTenses.length; j++) {
          for (let k = 0; k < this.selectedPronouns.length; k++)
            questions.push({
              verb: i,
              tense: this.selectedTenses[j],
              pronoun: this.selectedPronouns[k],
            });
        }
      }

      for (let i = questions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [questions[i], questions[j]] = [questions[j], questions[i]];
      }

      this.allQuestions = questions;
    },
    async newQuestion() {
      this.isSpeechCorrect = false; // reset speech recognition state
      if (this.remainingLives <= 0) {
        await this.endGame();
        return;
      }

      if (this.currentQuestion && !this.currentQuestion.answer) {
        console.warn("You must answer the current question first!");
        return;
      }

      if (this.nextQuestionTimeout) {
        clearTimeout(this.nextQuestionTimeout);
        this.nextQuestionTimeout = null;
      }

      if (this.questionsAsked.length >= this.allQuestions.length - 1) {
        this.toast.warning("All questions have been asked!");
        return;
      }

      this.pickedVerb = null;

      // const q = this.getRandomExample();
      //next question in suffled list
      this.questionsAsked.push(this.allQuestions[this.questionsAsked.length]);
      this.isHintShown = false;

      this.$nextTick(() => {
        window.scrollTo({
          top:
            document.body.scrollHeight || document.documentElement.scrollHeight,
          behavior: "smooth", // Optional: makes the scroll smooth
        });
      });
      setTimeout(() => {
        this.$nextTick(() => {
          (this.$refs.mobileView || this.$refs.scoreboard).scrollIntoView({
            behavior: "smooth",
          });
        });
      }, 300);
    },
    // getRandomExample() {
    //   const { verb, tense, pronoun } =
    //     this.allQuestions[this.questionsAsked.length];
    //   this.selectedExample = this.asExample(
    //     this.allVerbs[verb].conjugations[tense][pronoun]
    //   );
    //   return { verb, tense, pronoun };
    // },
    async checkAnswer(verb, tense, pronoun) {
      let q = this.questionsAsked[this.questionsAsked.length - 1];
      const theirs =
        this.allVerbs[verb].conjugations[tense][pronoun].conjugation;
      const real =
        this.allVerbs[q.verb].conjugations[q.tense][q.pronoun].conjugation;
      q.correct = theirs === real;
      q.answer = { verb, tense, pronoun, theirs, real };

      if (q.correct) {
        this.pickedVerb = verb;
        this.score += this.isHintShown ? 10 : 30;
        sounds.correct();
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
      } else if (q.verb == verb) {
        this.remainingLives -= 1;

        if (this.difficulty !== "expert") {
          this.score += this.isHintShown ? 0 : 10;
        } else {
          this.remainingLives -= 1;
        }
      } else {
        sounds.wrong();
        this.remainingLives -= 2;
      }

      if (this.remainingLives <= 0) {
        this.toast.error("‼️ GAME OVER ‼️", {
          timeout: 1000,
          closeOnClick: true,
          pauseOnHover: true,
          position: "top-right",
        });
        if (this.isAutoSkip) await this.endGame();
        return;
      }
      if (this.isAutoSkip)
        this.nextQuestionTimeout = setTimeout(
          () => {
            this.newQuestion();
          },
          q.correct ? 2500 : 6300
        );
      // this.newQuestion();
    },
    async endGame() {
      if (this.score) {
        let un = "anonymous";

        if (this.score >= this.leaderboardMinScore) {
          sounds.leaderboard();
          un = await this.promptForUsername();
        } else {
          sounds.gameover();
        }
        un = un?.trim() || "anonymous";

        this.$store.dispatch("updateUsername", `${un} (${this.difficulty})`);

        try {
          await this.$store.dispatch("saveScore");
        } catch (e) {
          console.error("Error saving score:", e);
        }
        this.$nextTick(() => {
          this.$refs.leaderboard.$el.scrollIntoView({
            behavior: "smooth",
          });

          if (this.score >= this.$store.getters.topScores?.[2]?.score) {
            this.top3Anim();
            sounds.top3();
          }
        });
      }

      this.isGameMode = false;
    },
    async promptForUsername() {
      return this.$refs.promptDialog.show();
    },
    async onTranscript(transcript) {
      const wasCorrect = await this.processSpeech(transcript);
      if (wasCorrect) this.isSpeechCorrect = true;
    },

    onSpeechEnd() {
      if (!this.isSpeechCorrect) {
        this.toast.error("Incorrect answer. Try again!");
      }
      this.isSpeechCorrect = false; // reset for next round
    },

    async processSpeech(transcript) {
      if (!this.isGameMode) return false;

      const { verb, tense, pronoun } = this.currentQuestion;
      const correctAnswer =
        this.allVerbs[verb].conjugations[tense][pronoun].conjugation;

      const normalize = (text) =>
        text
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");

      const normalizedTranscript = normalize(transcript);
      const normalizedAnswer = normalize(correctAnswer);
      const normalizedExample = normalize(this.selectedExample.es);

      const words = normalizedTranscript.split(/\s+/);

      if (
        normalizedTranscript === normalizedExample ||
        normalizedTranscript === normalizedAnswer ||
        words.includes(normalizedAnswer)
      ) {
        this.checkAnswer(verb, tense, pronoun);
        return true;
      }

      return false;
    },

    async processSpeechOld(transcript) {
      if (!this.isGameMode) return;

      const { verb, tense, pronoun } = this.currentQuestion;
      const correctAnswer =
        this.allVerbs[verb].conjugations[tense][pronoun].conjugation;

      // Normalize function
      const normalize = (text) =>
        text
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");

      const normalizedTranscript = normalize(transcript);
      const normalizedAnswer = normalize(correctAnswer);
      const normalizedExample = normalize(this.selectedExample.es);

      // Split transcript into words for partial match
      const words = normalizedTranscript.split(/\s+/); // split by whitespace

      if (normalizedTranscript === normalizedExample) {
        this.checkAnswer(verb, tense, pronoun);
        this.toast.success("Guau, esto es increíble!");
      } else if (normalizedTranscript === normalizedAnswer) {
        this.checkAnswer(verb, tense, pronoun);
      } else if (words.includes(normalizedAnswer)) {
        // exact word match
        this.checkAnswer(verb, tense, pronoun);
      } else if (
        words.some((word) => word.startsWith(normalizedAnswer.slice(0, 3)))
      ) {
        // this.speakText(correctAnswer);
        this.toast.info("Almost! Try again.");
      } else {
        // this.speakText(correctAnswer);
        // this.toast.error("Incorrect answer. Try again!");
      }
    },
    async playAudioFile() {
      if(this.isPlayingAudio) return; // Prevent multiple clicks
      const { verb, tense, pronoun } = this.currentQuestion;
      let fileName = `1/${this.allVerbs[verb].name}_${tense}_${pronoun}.wav`; // Construct the filename
      fileName = fileName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

      if (!fileName) {
        console.error("No file name provided for audio playback.");
        this.toast.error("No audio file available for this question.");
        return;
      }
      try {
        this.isPlayingAudio = true; // Set flag to prevent multiple clicks
        // Fetch the file URL from Supabase Storage
        const { data, error } = await supabase.storage
          .from("voices") // Assuming the bucket is named 'sounds'
          .getPublicUrl(fileName);
        if (error) {
          this.speakText(this.selectedExample.es);
          this.isPlayingAudio = false; // Reset flag on error
          console.error("Error fetching audio:", error.message);
          return;
        }

        // Play the sound file
        const audio = new Audio(data.publicUrl);
        audio.play();
        audio.onerror = (e) => {
          this.speakText(this.selectedExample.es);
          this.isPlayingAudio = false; // Reset flag on error
          console.error("Error playing audio:", e);
        };

        // Optional: Add a callback for when the audio ends (if needed)
        audio.onended = () => {
          this.isPlayingAudio = false; // Reset flag when audio ends
         
          this.isHintShown = true; // Your existing logic
        };
      } catch (err) {
        this.speakText(this.selectedExample.es);
        this.isPlayingAudio = false; // Reset flag on error
        console.error("Error playing audio:", err);
      }
    },
    speakText(text) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "es-ES";
      utterance.rate = 0.75;
      speechSynthesis.speak(utterance);
      utterance.onend = () => {
        this.isHintShown = true;
      };
    },
    getAnswerButtonClasses(pronoun, tKey) {
      if (!this.currentChosenAnswer) return "btn-info";

      const button =
        this.allVerbs[this.pickedVerb].conjugations[tKey][pronoun].conjugation;
      const isClicked = this.currentChosenAnswer.theirs === button;
      const isCorrect = this.currentChosenAnswer.real === button;

      const isActualClicked =
        this.currentChosenAnswer.verb === this.pickedVerb &&
        this.currentChosenAnswer.tense === tKey &&
        this.currentChosenAnswer.pronoun === pronoun;

      return {
        // Correct answer (button clicked or not)
        "btn-success bounce ": isCorrect,

        // Incorrect answer (button clicked)
        "btn-danger shake": !isCorrect && isClicked,

        // Fade and shrink other buttons that are not selected and incorrect
        "fade-buttons btn-dark": !isCorrect && !isActualClicked,
      };
    },

    top3Anim() {
      var end = Date.now() + 8 * 1000;

      // go Buckeyes!
      var colors = ["#bb0000", "#ffffff"];

      (function frame() {
        confetti({
          particleCount: 2,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors,
        });
        confetti({
          particleCount: 2,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors,
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      })();
    },
  },
};
</script>
  
  <style scoped>
table {
  width: 100%;
  border-collapse: collapse;
}
th,
td {
  padding: 8px;
  text-align: center;
}
th {
  background-color: #f4f4f4;
}
th em {
  font-weight: normal;
  display: block;
  font-size: 12px;
  color: var(--text-muted);
}
.game-mode th em {
  display: none;
}
td {
  cursor: pointer;
}

.game-mode td:hover {
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  transition: background-color 0.3s ease;
}

.scoreboard {
  font-size: 1.2em;
  /* margin-bottom: 15px; */
  position: sticky;
  top: 0;
  z-index: 10; /* Ensure it's above other content */
  background-color: #fffffff0; /* Optional: Adds a white background */
  padding: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1); /* Optional: Adds a shadow for better visibility */
}

.blur {
  filter: blur(6px);
  cursor: pointer;
}

.blur:hover {
  filter: blur(5px);
}

.progress {
  margin-bottom: 20px;
}

.progress-bar {
  transition: width 0.5s ease;
}
.heart {
  font-size: 14px;
  /* margin: 0 5px; */
}

.mobile-version {
  /* height: 100vh; */
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  scroll-margin-top: 75px;
}

.mobile-selection {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  max-width: 256px;
  margin: 0 auto;
  padding: 50px 0; /* Space for the alert */
}

.game-view.game-mode {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.verb-col-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* max-width: 200px; */
}

.btn-answer {
  transition: all 0.3s ease-in-out;
}

.pronouns-table-item {
}
.pronouns-table-item:hover {
  background-color: var(--bs-secondary);
  color: var(--bs-body);
  cursor: pointer;
  font-weight: bold;
}
.mode-label {
  width: 50px;
  left: calc(50% - 25px);
}

.question {
  font-size: 1rem;
}
.question-es {
  font-size: 0.875rem;
}
@media screen and (min-width: 576px) {
  .heart {
    font-size: 24px;
  }
  .question {
    font-size: 1.2em;
  }
  .question-es {
    font-size: 1rem;
  }

  .lives-container,
  .score {
    min-width: 100px;
  }
  .mobile-selection {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
    max-width: 384px;
    margin: 0 auto;
    padding: 70px 0; /* Space for the alert */
  }
}

.shake {
  animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  transform: translate3d(0, 0, 0);
}

@keyframes shake {
  10%,
  90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(2px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(4px, 0, 0);
  }
}

.bounce {
  animation: bounce 0.6s ease-out forwards;
}

@keyframes bounce {
  0% {
    transform: translateY(0);
  }
  20% {
    transform: translateY(-10px);
  }
  40% {
    transform: translateY(0);
  }
  60% {
    transform: translateY(-6px);
  }
  80% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-3px) scale(1.1);
  }
}

.fade-buttons {
  opacity: 0.3 !important;
  transform: scale(0.7);

  transition: opacity 0.2s ease, transform 0.5s ease;
}
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    transform: scale(1.2); /* Slightly larger */
    opacity: 0.85; /* Slightly faded */
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.pulse {
  transform: scale(1.2);

  animation: pulse 1s ease-out infinite; /* Infinite loop of pulse */
}

.transition-all {
  transition: all 0.3s ease-in-out;
}
</style>
  